// WebSocket integration for real-time poker table state
import { useTableStore } from '../state/tableStore';
import SockJS from 'sockjs-client';

export class PokerSocket {
  private socket: any | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private maxReconnectAttempts = 5;
  private baseReconnectDelayMs = 1000;
  private isOpen = false;
  private hasSockJsFallback = false;
  constructor(url: string) {
    this.url = url;
  }
  onError?: (err: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: () => void;
  connect() {
    if (this.url.startsWith('ws')) {
      this.connectNative(this.url);
      return;
    }
    this.connectSockJs(this.url);
  }
  close() {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.hasSockJsFallback = false;
    if (this.socket) {
      this.socket.close();
    }
  }
  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    const delay = this.baseReconnectDelayMs * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }
  send(data: any) {
    if (this.socket && this.socket.readyState === 1) {
      this.socket.send(JSON.stringify(data));
    }
  }
  private connectSockJs(url: string) {
    this.socket = new SockJS(url, undefined, { transports: ['xhr-polling'] });
    this.socket.onopen = () => {
      this.isOpen = true;
      if (this.onOpen) this.onOpen();
      this.reconnectAttempts = 0;
    };
    this.socket.onmessage = (event: MessageEvent) => {
      this.handleMessageSafe(event.data);
    };
    this.socket.onerror = (err: Event) => {
      if (this.onError) this.onError(err);
    };
    this.socket.onclose = (event: CloseEvent) => {
      this.isOpen = false;
      if (this.onClose) this.onClose(event);
      this.scheduleReconnect();
    };
  }
  private connectNative(url: string) {
    this.socket = new WebSocket(url);
    this.socket.onopen = () => {
      this.isOpen = true;
      if (this.onOpen) this.onOpen();
      this.reconnectAttempts = 0;
    };
    this.socket.onmessage = (event: MessageEvent) => {
      this.handleMessageSafe(event.data);
    };
    this.socket.onerror = (err: Event) => {
      if (!this.isOpen && !this.hasSockJsFallback) {
        this.fallbackToSockJs();
        return;
      }
      if (this.onError) this.onError(err as Event);
    };
    this.socket.onclose = (event: CloseEvent) => {
      this.isOpen = false;
      if (!this.hasSockJsFallback && !this.isOpen) {
        this.fallbackToSockJs();
        return;
      }
      if (this.onClose) this.onClose(event);
      this.scheduleReconnect();
    };
  }
  private fallbackToSockJs() {
    if (this.hasSockJsFallback) return;
    this.hasSockJsFallback = true;
    const fallbackUrl = this.url
      .replace(/^wss:\/\//, 'https://')
      .replace(/^ws:\/\//, 'http://');
    this.connectSockJs(fallbackUrl);
  }
  private handleMessageSafe(data: any) {
    let msg: any = data;
    if (typeof data === 'string') {
      try {
        msg = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!msg || typeof msg !== 'object') return;
    this.handleMessage(msg);
  }
  private handleMessage(msg: any) {
    const store = useTableStore.getState?.();
    if (!store) return;
    switch (msg.type) {
      case 'joined_table':
        if (typeof store.setTableId === 'function') store.setTableId(String(msg.tableId ?? '1'));
        break;
      case 'table_state':
        if (typeof store.updateState === 'function') {
          const community = Array.isArray(msg.community) ? msg.community : [];
          const phase = msg.phase === 'running'
            ? community.length >= 5
              ? 'river'
              : community.length === 4
                ? 'turn'
                : community.length === 3
                  ? 'flop'
                  : 'preflop'
            : 'idle';
          const seats = Array.isArray(msg.seats) ? msg.seats : [];
          const players = seats
            .filter((seat: any) => seat && seat.userId)
            .map((seat: any) => ({
              id: String(seat.userId),
              name: String(seat.displayName ?? seat.userId),
              chips: Number(seat.chips ?? 0),
              seat: Number(seat.seat ?? 0),
              avatarUrl: seat.avatarUrl ?? undefined,
              isActive: Boolean(seat.active ?? true),
              isEliminated: !Boolean(seat.active ?? true),
            }));
          store.updateState({
            tableId: String(msg.tableId ?? store.tableId ?? '1'),
            communityCards: community,
            pot: Number(msg.pot ?? 0),
            currentPlayerSeat: Number(msg.currentTurnIndex ?? -1),
            dealerSeat: Number(msg.dealerIndex ?? 0),
            seats,
            players,
            phase,
          });
        }
        break;
      case 'action_request':
        if (typeof store.setActionRequest === 'function') {
          store.setActionRequest(
            Number(msg.currentTurnIndex ?? -1),
            Number(msg.currentBet ?? 0),
            Number(msg.lastRaiseAmount ?? 0),
            Number(msg.remainingSeconds ?? 0),
            Number(msg.actionTimeoutSeconds ?? 0)
          );
        }
        break;
      case 'hand_result':
        if (typeof store.setHandResult === 'function') {
          store.setHandResult({
            pots: Array.isArray(msg.pots) ? msg.pots : [],
            community: Array.isArray(msg.community) ? msg.community : [],
          });
        }
        break;
      case 'error':
      case 'warning':
        if (typeof store.setNotification === 'function') {
          store.setNotification(String(msg.message ?? 'Server message'));
        }
        break;
      case 'private_state':
        if (typeof store.setPrivateState === 'function') {
          const seat = Number(msg.seat ?? -1);
          const incomingCards = Array.isArray(msg.holeCards) ? msg.holeCards : [];
          const currentCards = store.myHoleCards ?? [];
          const nextCards = incomingCards.length > 0 ? incomingCards : currentCards;
          store.setPrivateState(seat, nextCards);
        }
        break;
      case 'player_auto_fold':
        if (typeof store.updateState === 'function') {
          const seats = store.seats.map((seat: any) =>
            seat.seat === msg.seat ? { ...seat, active: false } : seat
          );
          store.updateState({ seats });
        }
        break;
      case 'deal':
        if (typeof store.dealCards === 'function') store.dealCards(msg.cards ?? []);
        break;
      case 'phase':
        if (typeof store.setPhase === 'function') store.setPhase(msg.phase);
        break;
      case 'pot':
        if (typeof store.setPot === 'function') store.setPot(msg.pot ?? 0);
        break;
      case 'current_player':
        if (typeof store.setCurrentPlayer === 'function') store.setCurrentPlayer(msg.seat ?? 0);
        break;
      case 'community_cards':
        if (typeof store.setCommunityCards === 'function') store.setCommunityCards(msg.cards ?? []);
        break;
      // Add more message types as needed
    }
  }
}
